import { useState, type FormEvent } from "react";
import useSWR from "swr";
import type { ExecTemplate, ExecVariable } from "./exec-template-editor";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { ListLoading } from "@/components/list/status-text";
import { UsersSelector } from "@/components/users-selector";
import { useUsers } from "@/hooks/users";
import { useToast } from "@/hooks/use-toast";
import { postFetcher } from "@/lib/swr";
import { toastError } from "@/lib/toast";

export const PUBLICATIONS_PATH = "/exec-template-publications";
export const TEMPLATE_CONFIG_PATH = `${PUBLICATIONS_PATH}/config`;
export const MY_TEMPLATES_PATH = "/me/exec-templates";

export type TemplateSharingConfig = {
  enabled: boolean;
  variables: ExecVariable[];
};
export type ExecPublication = {
  id: string;
  publisherId: string;
  name: string;
  body?: string;
  audience: "all" | "users";
  recipientIds?: string[];
  sourceTemplateId?: string;
  installedTemplateId?: string | null;
  createdAt: string;
  updatedAt: string;
};
export type PublicationList = { data: ExecPublication[]; total: number };

const PublishForm = ({
  template,
  publication,
  onSaved,
}: {
  template: ExecTemplate;
  publication: ExecPublication | null;
  onSaved: (p: ExecPublication) => void;
}) => {
  const { toast } = useToast();
  const users = useUsers();
  const [audience, setAudience] = useState<"all" | "users">(
    publication?.audience ?? "all",
  );
  const [recipientIds, setRecipientIds] = useState<string[]>(
    publication?.recipientIds ?? [],
  );
  const [saving, setSaving] = useState(false);
  const selected = recipientIds.map(
    (id) => users.find((u) => u.id === id) ?? { id, name: id },
  );
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (saving || (audience === "users" && !recipientIds.length)) return;
    setSaving(true);
    try {
      const saved = await postFetcher<ExecPublication>(
        `${MY_TEMPLATES_PATH}/${template.id}/publish`,
        {
          arg: {
            audience,
            recipientIds: audience === "users" ? recipientIds : [],
          },
        },
      );
      toast({ title: publication ? "已更新发布内容" : "模板已发布" });
      onSaved(saved);
    } catch (e) {
      toastError(toast, "发布模板失败", e);
    } finally {
      setSaving(false);
    }
  };
  return (
    <form onSubmit={submit} className="grid gap-4">
      <div className="grid gap-2">
        <Label id="publish-audience-label">发布给</Label>
        <RadioGroup
          aria-labelledby="publish-audience-label"
          value={audience}
          onValueChange={(value) => setAudience(value as "all" | "users")}
        >
          <div className="flex items-center gap-2">
            <RadioGroupItem value="all" id="publish-all" disabled={saving} />
            <Label htmlFor="publish-all">全员</Label>
          </div>
          <div className="flex items-center gap-2">
            <RadioGroupItem
              value="users"
              id="publish-users"
              disabled={saving}
            />
            <Label htmlFor="publish-users">指定员工</Label>
          </div>
        </RadioGroup>
      </div>
      {audience === "users" && (
        <div className="space-y-2">
          <UsersSelector
            selectedUsers={selected}
            hideUsers={users.filter((u) => u.kind === "bot")}
            buttonText="选择员工"
            onContinue={(selected) =>
              setRecipientIds(selected.map((u) => String(u.id)))
            }
          />
          <p className="text-sm text-muted-foreground">
            {selected.length
              ? selected.map((u) => u.name ?? u.id).join("、")
              : "请选择至少一名员工"}
          </p>
        </div>
      )}
      <DialogFooter>
        <Button
          type="submit"
          disabled={saving || (audience === "users" && !recipientIds.length)}
        >
          {saving ? "发布中…" : publication ? "再次发布" : "发布"}
        </Button>
      </DialogFooter>
    </form>
  );
};

export const PublishTemplateDialog = ({
  template,
  onOpenChange,
  onSaved,
}: {
  template: ExecTemplate;
  onOpenChange: (open: boolean) => void;
  onSaved: () => void;
}) => {
  const { data, error, mutate } = useSWR<{ publication: ExecPublication | null }>([
    `${MY_TEMPLATES_PATH}/${template.id}/publication`,
  ]);
  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>发布模板「{template.name}」</DialogTitle>
          <DialogDescription>
            发布当前已保存的内容。同事安装后获得自己的副本；再次发布或撤回不会改变这些副本。
          </DialogDescription>
        </DialogHeader>
        {error ? (
          <div role="alert" className="space-y-2 text-sm">
            <p>加载发布设置失败</p>
            <Button variant="outline" size="sm" onClick={() => void mutate()}>
              重试
            </Button>
          </div>
        ) : data === undefined ? (
          <ListLoading>加载发布设置…</ListLoading>
        ) : (
          <PublishForm
            key={`${data.publication?.id ?? "new"}:${data.publication?.updatedAt ?? ""}`}
            template={template}
            publication={data.publication}
            onSaved={(saved) => {
              void mutate({ publication: saved }, false);
              onSaved();
              onOpenChange(false);
            }}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};
